import { useQuery } from "@tanstack/react-query";

export interface ImpersonationState {
  isImpersonating: boolean;
  isViewOnly: boolean;
  isEditEnabled: boolean;
  isGhost: boolean;
  impersonatingUser: { id: string; username: string; companyName: string | null } | null;
  pendingEditRequestId: string | null;
}

export function useImpersonation(): ImpersonationState {
  const { data: user } = useQuery<any>({
    queryKey: ["/api/auth/me"],
    retry: false,
    // Poll every 10 seconds so the banner updates promptly when the dealer
    // approves or denies an edit-access request without requiring a page reload.
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const isImpersonating = !!user?.impersonatingUserId;
  const isGhost = isImpersonating && !!user?.ghostMode;
  const isEditEnabled = isImpersonating && !isGhost && !!user?.editAccessApproved;
  const isViewOnly = isImpersonating && !isEditEnabled;

  return {
    isImpersonating,
    isViewOnly,
    isEditEnabled,
    isGhost,
    impersonatingUser: user?.impersonatingUser ?? null,
    pendingEditRequestId: user?.pendingEditRequestId ?? null,
  };
}

/**
 * Returns props that disable an interactive element when the admin is in
 * view-only impersonation mode.  Spread onto any Button/input that triggers
 * a mutation so that the disabled state is explicit in the React tree in
 * addition to the CSS-level blocking applied to [data-view-only="true"].
 *
 * Usage:  <Button {...viewOnlyProps()} onClick={handleSave}>Save</Button>
 */
export function useViewOnlyProps(): Record<string, unknown> {
  const { isViewOnly } = useImpersonation();
  return isViewOnly ? { disabled: true, "aria-disabled": true } : {};
}
